<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="inertia-link"
                :pages="[
                    {
                        name: $tc('messages.activities', 2),
                        href: meta.links.index,
                    },
                    {
                        name: activity.title,
                        href: null,
                    },
                ]"
            />
        </template>

        <div class="w-full flex justify-end space-x-2">
            <span
                class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium leading-4 bg-yellow-100 text-yellow-800"
                v-if="activity.draft"
            >
                {{ $t('messages.draft') }}
            </span>
            <InertiaLinkButton type="white" :href="meta.links.duplicate" as="button" method="post">
                {{ $t('messages.duplicate') }}
            </InertiaLinkButton>
            <AppButton element="InertiaLink" :href="meta.links.edit">
                {{ $t('messages.edit') }}
            </AppButton>
        </div>

        <TitleFieldsFormSection>
            <template v-slot:title>
                {{ $t('messages.information') }}
            </template>
            <div class="md:grid md:grid-cols-12 md:gap-6">
                <div class="sm:col-span-2">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.grade_level')" for="subject_id" />
                        {{ activity.grade_level }}
                    </div>
                </div>
                <div class="sm:col-span-10">
                    <div class="mt-1">
                        <FormLabel :value="$tc('messages.subject', 1)" for="subject_id" />
                        {{ activity.subject }}
                    </div>
                </div>
            </div>
        </TitleFieldsFormSection>

        <TitleFieldsFormSection>
            <template v-slot:title>
                {{ $t('messages.dates_information') }}
            </template>
            <div class="md:grid md:grid-cols-12 md:gap-6">
                <div class="sm:col-span-4">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.trimester')" for="trimester" />
                        {{ activity.trimester }}
                    </div>
                </div>
            </div>
        </TitleFieldsFormSection>

        <TitleFieldsFormSection v-if="activity.comment">
            <template v-slot:title>
                {{ $t('messages.comment') }}
                <p class="mt-1 text-sm leading-5 text-gray-500">
                    {{ $t('messages.information_only_visible_by_teachers') }}
                </p>
            </template>
            <div class="md:grid md:grid-cols-12 md:gap-6">
                <div class="sm:col-span-12">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.comment')" for="comment" class="sr-only" />
                        <p v-html="activity.comment" />
                    </div>
                </div>
            </div>
        </TitleFieldsFormSection>

        <TitleFieldsFormSection v-if="activity.description">
            <template v-slot:title>
                {{ $t('messages.description') }}
                <p class="mt-1 text-sm leading-5 text-gray-500">
                    {{ $t('messages.information_visible_by_students') }}
                </p>
            </template>
            <div class="md:grid md:grid-cols-12 md:gap-6">
                <div class="sm:col-span-12">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.description')" for="description" class="sr-only" />
                        <p v-html="activity.description" />
                    </div>
                </div>
            </div>
        </TitleFieldsFormSection>

        <TitleFieldsFormSection v-if="activity.attention_to_diversity">
            <template v-slot:title>
                {{ $t('messages.attention_to_diversity') }}
                <p class="mt-1 text-sm leading-5 text-gray-500">
                    {{ $t('messages.information_only_visible_by_teachers') }}
                </p>
            </template>
            <div class="md:grid md:grid-cols-12 md:gap-6">
                <div class="sm:col-span-12">
                    <div class="mt-1">
                        <FormLabel
                            :value="$t('messages.attention_to_diversity')"
                            for="attention_to_diversity"
                            class="sr-only"
                        />
                        <p v-html="activity.attention_to_diversity" />
                    </div>
                </div>
            </div>
        </TitleFieldsFormSection>

        <TitleFieldsFormSection v-if="activity.accompanying_text || activity.image_path">
            <template v-slot:title>
                {{ $t('messages.accompanying_text') }}
                <p class="mt-1 text-sm leading-5 text-gray-500">
                    {{ $t('messages.information_visible_by_students') }}
                </p>
            </template>
            <div class="md:grid md:grid-cols-12 md:gap-6" v-if="activity.accompanying_text">
                <div class="sm:col-span-12">
                    <div class="mt-1">
                        <p v-html="activity.accompanying_text" class="prose" />
                    </div>
                </div>
            </div>
            <div class="sm:col-span-12" v-if="activity.image_path">
                <div class="mt-1">
                    <img :src="activity.image_path" alt="cover" class="h-auto" />
                </div>
            </div>
        </TitleFieldsFormSection>

        <TitleFieldsFormSection v-if="activity.weight">
            <template v-slot:title>
                {{ $t('messages.score') }}
            </template>
            <div class="md:grid md:grid-cols-12 md:gap-6">
                <div class="sm:col-span-6">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.max_score')" for="max_score" />
                        {{ activity.max_score_display }}
                    </div>
                </div>
            </div>
        </TitleFieldsFormSection>

        <TitleFieldsFormSection v-if="activity.materials.length > 0">
            <template v-slot:title>
                {{ $tc('messages.materials', 2) }}
            </template>
            <div class="md:grid md:grid-cols-12 md:gap-6">
                <div class="sm:col-span-12">
                    <ul class="">
                        <li v-for="(row, index) in activity.materials" :key="index">
                            <span v-if="!row.url">
                                {{ row.title }}
                            </span>
                            <a :href="row.url" target="_blank" class="text-indigo-500" v-else>
                                <svg
                                    class="w-4 h-4 inline-block"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z"
                                    ></path>
                                    <path
                                        d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z"
                                    ></path>
                                </svg>
                                <span class="truncate text-ellipsis overflow-hidden">
                                    {{ row.title ? row.title : row.url }}
                                </span>
                            </a>
                        </li>
                    </ul>
                </div>
            </div>
        </TitleFieldsFormSection>

        <TitleFieldsFormSection>
            <template v-slot:title>
                {{ $t('messages.advanced_options') }}
            </template>

            <div class="md:grid md:grid-cols-12 md:gap-6">
                <div class="sm:col-span-3">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.score_weight')" for="weight" />
                        {{ activity.display_weight }}
                    </div>
                </div>

                <div class="sm:col-span-3">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.visibility')" for="visibility" />
                        {{ activity.display_visibility }}
                    </div>
                </div>

                <div class="sm:col-span-3" v-if="activity.grouping">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.grouping')" for="grouping" />
                        {{ activity.grouping_display }}
                    </div>
                </div>

                <div class="sm:col-span-3" v-if="activity.duration">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.duration')" for="duration" />
                        {{ activity.duration }}
                    </div>
                </div>
            </div>
        </TitleFieldsFormSection>

        <TitleFieldsFormSection>
            <template v-slot:title>
                {{ $tc('messages.evaluation_criteria', 2) }}
            </template>
            <div>
                <div class="flex flex-col mt-5">
                    <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                        <div
                            class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                        >
                            <table class="min-w-full divide-y divide-gray-200">
                                <thead>
                                    <tr>
                                        <th
                                            class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                        >
                                            {{ $tc('messages.evaluation_criteria', 1) }}
                                        </th>
                                        <th
                                            class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                        >
                                            {{ $t('messages.max_score') }}
                                        </th>
                                        <th
                                            class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                        >
                                            <span class="sr-only">{{ $t('messages.actions') }}</span>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr
                                        :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                                        v-for="(subactivity, index) in activity.subactivities"
                                        :key="subactivity.id"
                                    >
                                        <td
                                            class="px-6 py-4 whitespace-nowrap text-sm leading-5"
                                            :title="subactivity.title"
                                        >
                                            {{ subactivity.name }}
                                        </td>
                                        <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                            {{ subactivity.max_score }}
                                        </td>
                                        <td
                                            class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500 text-right flex space-x-2 justify-end items-center"
                                        >
                                            <InertiaSquareLinkButton
                                                action="quiz"
                                                :href="subactivity.links.evaluation_criterion_edit"
                                                :title="$t('messages.assign_evaluation_value')"
                                                v-if="subactivity.evaluation_criterion_id === null"
                                            />
                                            <SubactivityEditForm
                                                :endpoint="subactivity.update_endpoint"
                                                :subactivity="subactivity"
                                                is-edit
                                                v-if="meta.can.edit_evaluation_criteria"
                                            />
                                            <InertiaSquareConfirmActionButton
                                                :href="subactivity.links.delete"
                                                danger
                                                :confirm-text="$t('messages.delete')"
                                                method="delete"
                                                v-if="meta.can.edit_evaluation_criteria"
                                            >
                                                <template v-slot:icon>
                                                    <svg viewBox="0 0 20 20" fill="currentColor" class="trash w-4 h-4">
                                                        <path
                                                            fill-rule="evenodd"
                                                            d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                                                            clip-rule="evenodd"
                                                        ></path>
                                                    </svg>
                                                </template>
                                                <div>
                                                    {{ $t('messages.delete') }}
                                                </div>
                                                <AlertMessage
                                                    class="mt-5 inline-block"
                                                    v-if="subactivity.subactivity_responses_count"
                                                >
                                                    <template v-slot:title>
                                                        {{ $t('messages.warning') }}
                                                    </template>
                                                    <template v-slot:body>
                                                        {{ subactivity.confirm_deletion_message }}
                                                    </template>
                                                </AlertMessage>
                                            </InertiaSquareConfirmActionButton>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </TitleFieldsFormSection>

        <TitleFieldsFormSection>
            <template v-slot:title>
                {{ $tc('messages.dimensions', 2) }}
                <br />
                <AppButton
                    v-if="meta.can.edit_evaluation_criteria"
                    :href="meta.links.evaluation_criteria_edit"
                    element="inertia-link"
                    :desired-action="activity.activityDimensions.length === 0"
                >
                    {{ $t('messages.assign_evaluation_value') }}
                </AppButton>
            </template>
            <div class="md:grid md:grid-cols-12 md:gap-6">
                <div class="sm:col-span-12">
                    <div
                        class="mt-5 md:mt-0 md:col-span-2"
                        v-for="category in activity.activityDimensions"
                        :key="category.id"
                    >
                        <span class="font-medium">
                            {{ category.name }}
                        </span>
                        <ul class="pl-3">
                            <li v-for="dimension in category.dimensions" :key="dimension.id">
                                {{ dimension.abbreviation }} -
                                {{ dimension.title }}

                                <BadgePill>
                                    {{ dimension.effective_value }}
                                </BadgePill>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </TitleFieldsFormSection>

        <SyllabusSummary :summary="activity.syllabusSummary" show-counters />

        <TitleFieldsFormSection v-if="meta.links.edit_rubric">
            <template v-slot:title>
                {{ $tc('messages.rubrics', 2) }}
            </template>

            <div class="flex space-x-2 items-center">
                <AppButton
                    element="inertia-link"
                    :href="meta.links.edit_rubric"
                    :desired-action="!activity.rubric_levels"
                >
                    {{ activity.rubric_levels ? $t('messages.edit_rubric') : $t('messages.create_rubric') }}
                </AppButton>
                <AppButton element="a" :href="meta.links.rubric_print" v-if="activity.rubric_levels">
                    <IconPrinter class="-ml-0.5 mr-2 w-4 h-4" />
                    {{ $t('messages.template') }} - {{ $t('messages.print') }}
                </AppButton>

                <InertiaSquareLinkButton v-if="activity.rubric_levels" :href="meta.links.rubric_show" action="show" />

                <InertiaSquareDeleteButton
                    v-if="activity.rubric_levels"
                    :href="meta.links.delete_rubrics"
                    :confirm-text="$t('messages.are_you_sure?')"
                />
            </div>
        </TitleFieldsFormSection>

        <TitleFieldsFormSection v-if="toggles.length">
            <template v-slot:title>
                {{ $t('messages.settings') }}
            </template>

            <div class="mt-5">
                <div v-for="feature in toggles" :key="feature.url" class="mt-5">
                    <div>
                        <InertiaLink
                            method="patch"
                            as="button"
                            :href="feature.url"
                            preserve-scroll
                            :class="feature.is_active ? 'bg-indigo-600' : 'bg-gray-200'"
                            :only="['toggles']"
                            class="relative inline-flex shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            role="switch"
                            aria-checked="false"
                        >
                            <span class="sr-only">role</span>
                            <span
                                :class="feature.is_active ? 'translate-x-5' : 'translate-x-0'"
                                aria-hidden="true"
                                class="translate-x-0 pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow ring-0 transition ease-in-out duration-200"
                            ></span>
                        </InertiaLink>
                        {{ feature.display_name }}
                    </div>
                    <div v-if="feature.explanation">
                        <AlertMessage class="mt-5 inline-block" type="info">
                            <template v-slot:title>
                                {{ $t('messages.information') }}
                            </template>
                            <template v-slot:body> {{ feature.explanation }} </template>
                        </AlertMessage>
                    </div>
                </div>
            </div>
        </TitleFieldsFormSection>

        <TitleFieldsFormSection>
            <template v-slot:title>
                <span v-if="activity.type === 'default'">
                    {{ $t('messages.grade_activity') }}
                </span>
                <span v-else>
                    {{ $tc('messages.grades', 2) }}
                </span>
            </template>
            <div class="md:grid md:grid-cols-12 md:gap-6">
                <div class="sm:col-span-12">
                    <div v-if="activity.groupsToGrade.length === 0" class="text-gray-600">
                        {{ $t('messages.there_are_no_groups_on_this_grade') }}
                    </div>
                    <div class="w-full flex space-x-2" v-else>
                        <ButtonPrimary
                            :href="group.links.grade"
                            v-for="group in activity.groupsToGrade"
                            :key="group.name"
                        >
                            {{ group.name }}
                        </ButtonPrimary>
                    </div>
                </div>
            </div>
        </TitleFieldsFormSection>

        <TitleFieldsFormSection v-if="activity.is_quiz">
            <template v-slot:title>
                {{ $tc('messages.quizzes', 1) }}
            </template>
            <div class="md:grid md:grid-cols-12 md:gap-6">
                <div class="sm:col-span-12">
                    <AppButton :href="meta.links.add_questions" element="a" desired-action>
                        {{ $tc('messages.questions', 2) }}
                    </AppButton>
                </div>
            </div>
        </TitleFieldsFormSection>

        <TitleFieldsFormSection>
            <template v-slot:title>
                {{ $tc('messages.programmings', 2) }}
            </template>
            <div class="md:grid md:grid-cols-12 md:gap-6">
                <div class="sm:col-span-12">
                    <div v-if="activity.programmings.length === 0" class="text-gray-600">
                        <EmptyState :title="$t('messages.no_programming_associated')" with-icon with-button>
                            <template v-slot:button>
                                <AppButton element="InertiaLink" :href="route('programmings.index')">
                                    {{ $tc('messages.programmings', 2) }}
                                </AppButton>
                            </template>
                        </EmptyState>
                    </div>
                    <ul class="w-full flex space-x-2" v-else>
                        <li v-for="programming in activity.programmings" :key="programming.id">
                            <inertia-link :href="programming.links.show" class="text-indigo-500">
                                {{ programming.title }}
                            </inertia-link>
                        </li>
                    </ul>
                </div>
            </div>
        </TitleFieldsFormSection>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import TitleFieldsFormSection from '@/components/TitleFieldsFormSection.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import BadgePill from '@/components/BadgePill.vue';
import ButtonPrimary from '@/components/ButtonPrimary.vue';
import SyllabusSummary from '@/components/SyllabusSummary.vue';
import EmptyState from '@/components/EmptyState.vue';
import InertiaLinkButton from '@/components/InertiaLinkButton.vue';
import AppButton from '@/components/AppButton.vue';
import InertiaSquareConfirmActionButton from '@/components/InertiaSquareConfirmActionButton.vue';
import SubactivityEditForm from '@/components/pageSpecific/SubactivityEditForm.vue';
import InertiaSquareDeleteButton from '@/components/InertiaSquareDeleteButton.vue';
import InertiaSquareLinkButton from '@/components/InertiaSquareLinkButton.vue';
import AlertMessage from '@/components/AlertMessage.vue';
import { IconPrinter } from '@tabler/icons-vue';

export default {
    components: {
        PanelPage,
        TitleFieldsFormSection,
        FormLabel,
        ButtonPrimary,
        BadgePill,
        SyllabusSummary,
        EmptyState,
        InertiaLinkButton,
        InertiaSquareLinkButton,
        AppButton,
        SubactivityEditForm,
        InertiaSquareConfirmActionButton,
        InertiaSquareDeleteButton,
        AlertMessage,
        IconPrinter,
    },
    layout: Layout,
    name: 'Show',
    props: ['teacher', 'meta', 'options', 'activity', 'defaults', 'toggles'],
    data() {
        return {};
    },
    methods: {},
};
</script>
